import React, {
  BaseSyntheticEvent, useContext, useEffect, useState,
} from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import { useTranslation } from 'react-i18next';
import { CheckCircleOutline } from '@mui/icons-material';
import * as styles from './modal.styles';
import Row from '../../../core/components/row/row.component';
import Column from '../../../core/components/column/column.component';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import { CrossAxisAlignment } from '../../../core/components/flex/flex.component';
import saveFloorPlanForOffice, { getFloorPlanStoreys } from '../services/floor-plan.service';
import { firstFreeStorey, storeyProblemKey, storeyProblemOf } from '../../floorplans/utils/storey-validation';
import FloorPlan from '../models/floor-plan';
import notify from '../../../core/components/notify/notify.component';
import LabeledInput from '../../../core/components/input/labeled-input.component';
import { useKeycloak } from '../../authentication/keycloak/KeycloakProvider';
import DrawingImport from '../components/drawing-import.component';
import { EDITOR_FEATURES } from '../../floorplans/utils/editor-features';
import type { DrawingConversion } from '../services/drawing-import.service';

/** The five stored layers, in the order the manual lists them. */
type LayerKey = 'borderLayer' | 'floorLayer' | 'roomLayer' | 'workplacesLayer' | 'labelLayer';

const LAYERS: ReadonlyArray<{
  readonly key: LayerKey;
  readonly labelKey: string;
  /** The label layer is nullable in the database and absent from most stored floorplans. */
  readonly optional?: boolean;
}> = [
  { key: 'borderLayer', labelKey: 'floor-plan-border' },
  { key: 'floorLayer', labelKey: 'floor-plan-storey' },
  { key: 'roomLayer', labelKey: 'floor-plan-rooms' },
  { key: 'workplacesLayer', labelKey: 'floor-plan-workplaces' },
  { key: 'labelLayer', labelKey: 'floor-plan-labels', optional: true },
];

interface Props {
  readonly showModal: boolean;
  readonly dashboardOfficeId: string;
  readonly handleClose: () => void;
}

const FloorplanUploadModal = ({ showModal, dashboardOfficeId, handleClose }: Props): React.JSX.Element => {
  const { t } = useTranslation();
  const isMobile = useContext(IsMobileContext);
  const [floorPlan, setFloorPlan] = useState<FloorPlan>(new FloorPlan());
  // Kept as typed, so an empty field or a half-typed "-" is not silently read as floor 0.
  const [storeyText, setStoreyText] = useState('0');
  // The floors this office already has, read when the dialog opens so a taken one is refused
  // before anything is uploaded. The server refuses it too, should this not have arrived.
  const [takenStoreys, setTakenStoreys] = useState<number[]>([]);
  const [uploaded, setUploaded] = useState<Record<LayerKey, boolean>>({
    borderLayer: false,
    floorLayer: false,
    roomLayer: false,
    workplacesLayer: false,
    labelLayer: false,
  });
  const [importing, setImporting] = useState(false);
  const [unitsPerMetre, setUnitsPerMetre] = useState<number | null>(null);
  const keycloak = useKeycloak();

  useEffect(() => {
    if (!showModal) return undefined;

    let active = true;

    getFloorPlanStoreys(dashboardOfficeId, keycloak)
      .then((storeys) => {
        if (!active || !storeys) return;
        setTakenStoreys(storeys);
        setStoreyText(String(firstFreeStorey(storeys)));
      })
      .catch(() => undefined);

    return (): void => {
      active = false;
    };
  }, [showModal, dashboardOfficeId, keycloak]);

  const storeyProblem = storeyProblemOf(storeyText, takenStoreys);

  /**
   * Reads one uploaded GeoJSON file into the layer it belongs to.
   *
   * One handler rather than one per layer: they differed only in which field they wrote, and
   * the fifth — the label layer — was missing entirely, which is why annotations could be
   * exported from a floorplan and never uploaded back into one.
   */
  const handleUpload = (layer: LayerKey) => (uploadEvent: BaseSyntheticEvent): void => {
    const file = uploadEvent.target.files?.[0];

    if (!file) {
      return;
    }

    const fileReader = new FileReader();
    fileReader.readAsText(file);

    fileReader.onload = (loadEvent): void => {
      const contents = loadEvent.target?.result;

      if (typeof contents !== 'string') {
        return;
      }

      try {
        const parsed = JSON.parse(contents);

        // Held per layer rather than merged into one object, so a file that fails to parse
        // leaves the other four alone.
        setFloorPlan((current) => ({ ...current, [layer]: parsed }) as FloorPlan);
        setUploaded((current) => ({ ...current, [layer]: true }));
      } catch {
        // A file that is not JSON is the commonest mistake here — a .dxf or a .zip picked by
        // accident — and it used to fail silently, leaving a tick beside a layer holding
        // nothing.
        notify(t('floor-plan-upload-not-json', { file: file.name }), false);
        setUploaded((current) => ({ ...current, [layer]: false }));
      }
    };
  };

  /**
   * Takes the five layers a converted drawing produced.
   *
   * They land in exactly the same state a set of uploaded GeoJSON files would, so the
   * administrator sees what was produced, can still replace any single layer by hand, and
   * saves through the one path that creates a floorplan.
   */
  const handleConverted = (conversion: DrawingConversion): void => {
    setFloorPlan((current) => ({
      ...current,
      borderLayer: conversion.borderLayer,
      floorLayer: conversion.floorLayer,
      roomLayer: conversion.roomLayer,
      workplacesLayer: conversion.workplacesLayer,
      labelLayer: conversion.labelLayer,
    }) as FloorPlan);

    setUploaded({
      borderLayer: true,
      floorLayer: true,
      roomLayer: true,
      workplacesLayer: true,
      labelLayer: conversion.labelLayer !== null && conversion.labelLayer !== undefined,
    });

    // The drawing's own scale, carried through to the stored floorplan. Without it an
    // imported plan arrives as one nobody has measured, and every metre-denominated figure
    // in the editor is unavailable on a plan whose file said exactly what its units were.
    setUnitsPerMetre(conversion.unitsPerMetre);
  };

  const handleReset = (): void => {
    setFloorPlan(new FloorPlan());
    setImporting(false);
    setUnitsPerMetre(null);
    setUploaded({
      borderLayer: false,
      floorLayer: false,
      roomLayer: false,
      workplacesLayer: false,
      labelLayer: false,
    });
    handleClose();
  };

  const saveFloorPlan = async (): Promise<void> => {
    // The border is required too: the app sizes the whole floorplan off it, so a plan without
    // one renders as nothing at all. It was left out of this check while being one of the
    // four the database refuses as null, so the upload failed server-side instead.
    if (!uploaded.borderLayer || !uploaded.floorLayer || !uploaded.roomLayer || !uploaded.workplacesLayer) {
      notify(t('floor-plan-upload-error'), false);
      return;
    }

    if (storeyProblem) {
      notify(t(storeyProblemKey(storeyProblem), { storey: storeyText.trim() }), false);
      return;
    }

    const floorPlanCopy = { ...floorPlan };
    floorPlanCopy.officeId = dashboardOfficeId;
    floorPlanCopy.storey = Number(storeyText.trim());

    const result = await saveFloorPlanForOffice(floorPlanCopy, keycloak, unitsPerMetre);

    if (result !== 'saved') {
      const failures: Record<string, string> = {
        duplicateStorey: 'floor-plan-upload-duplicate-storey',
        invalidStorey: 'floorplan-storey-negative',
      };
      notify(t(failures[result] ?? 'floor-plan-error-message'), false);
      return;
    }

    handleReset();
    notify(t('floor-plan-upload-success'), true);
  };

  return (
    <Modal ariaHideApp={false} isOpen={showModal} style={styles.modalStyle(isMobile)}>
      <Column crossAxisAlignment={CrossAxisAlignment.Stretch} itemSpacing={12}>
        <span className={style(styles.columnHeader)}>{t('floor-plan')}</span>
        <span className={style(styles.bodyText)}>{t('floor-plan-upload-intro')}</span>
        <LabeledInput
          labelText={t('floor-plan-storey-number').toString()}
          value={storeyText}
          onChange={(value): void => {
            setStoreyText(value);
          }}
          type="number"
          min={0}
        />
        {storeyProblem && (
          <span className={style(styles.errorText)}>{t(storeyProblemKey(storeyProblem), { storey: storeyText.trim() })}</span>
        )}
        {/* GeoJSON only for now; the AutoCAD route comes back with its feature switch. */}
        {EDITOR_FEATURES.drawingImport && (
          <>
            <PrimaryButton fullWidth onClick={(): void => setImporting((current) => !current)}>
              {t(importing ? 'floor-plan-import-cad-hide' : 'floor-plan-import-cad')}
            </PrimaryButton>

            {importing && (
              <DrawingImport officeId={dashboardOfficeId} onConverted={handleConverted} />
            )}
          </>
        )}

        {LAYERS.map(({ key, labelKey, optional }) => (
          <Row key={key} itemSpacing={12}>
            <label className={style(styles.uploadText)} htmlFor={`${key}Upload`}>
              {optional ? `${t(labelKey)} (${t('optional')})` : t(labelKey)}
            </label>
            <input
              id={`${key}Upload`}
              type="file"
              accept=".geojson,.json,application/geo+json,application/json"
              onChange={handleUpload(key)}
            />
            <CheckCircleOutline className={style(styles.checkIcon(uploaded[key]))} />
          </Row>
        ))}
        <PrimaryButton
          fullWidth
          onClick={(): void => {
            saveFloorPlan();
          }}
        >
          {t('save')}
        </PrimaryButton>
        <PrimaryButton fullWidth onClick={handleReset}>
          {t('close')}
        </PrimaryButton>
      </Column>
    </Modal>
  );
};

export default FloorplanUploadModal;
